import { Badge, Popover, Typography } from "antd";
import type {
  ValidationStatusEnum,
  ValidationStatusOutput,
} from "../../../services/client/types.gen";
import {
  getStatusColor,
  getStatusText,
} from "../../util-components/validationStatusFormat";

const { Text } = Typography;

type ValidationIssue = {
  status: ValidationStatusEnum;
  source: string;
  message: string;
};

const validationIssues = (
  validationStatus: ValidationStatusOutput | null | undefined,
): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];
  const add = (
    status: ValidationStatusEnum | undefined,
    source: string,
    message: string,
  ) => {
    if (status && status !== "OK") {
      issues.push({ status, source, message });
    }
  };

  const configuration = validationStatus?.site_configuration_status;
  add(
    configuration?.configuration_added?.status,
    "Site configuration",
    "Site topology not configured.",
  );
  const missingAssets =
    configuration?.configuration_complete?.missing_assets ?? [];
  if (missingAssets.length > 0) {
    add(
      configuration?.configuration_complete?.status,
      "Configuration completeness",
      `Assets missing in configuration: ${missingAssets.join(", ")}.`,
    );
  }
  add(
    configuration?.valid_for_optimization?.status,
    "Valid for optimization",
    configuration?.valid_for_optimization?.validation_errors ??
      "Not validated for optimization yet.",
  );
  add(
    validationStatus?.optimization_configuration_status?.status,
    "Optimization configuration",
    "Optimization configuration not added.",
  );
  const receivingMeasurements =
    validationStatus?.ingress_status?.receiving_measurement_data;
  if (receivingMeasurements?.all_assets) {
    add(
      receivingMeasurements.status,
      "Ingress",
      "Not receiving any measurement data.",
    );
  } else if (receivingMeasurements?.not_receiving_data_from?.length) {
    add(
      receivingMeasurements.status,
      "Ingress",
      `Not receiving measurement data from: ${receivingMeasurements.not_receiving_data_from.join(", ")}.`,
    );
  }
  return issues;
};

type SiteValidationBadgeProps = {
  validationStatus?: ValidationStatusOutput | null;
};

export const SiteValidationBadge: React.FC<SiteValidationBadgeProps> = ({
  validationStatus,
}) => {
  const issues = validationIssues(validationStatus);
  const badge = (
    <Badge
      status={getStatusColor(validationStatus?.status)}
      text={getStatusText(validationStatus?.status)}
    />
  );

  if (issues.length === 0) {
    return badge;
  }

  return (
    <Popover
      title="Validation issues"
      content={
        <div className="flex max-h-72 max-w-md flex-col gap-3 overflow-auto">
          {issues.map((issue) => (
            <div key={issue.source}>
              <Badge
                status={getStatusColor(issue.status)}
                text={<Text strong>{issue.source}</Text>}
              />
              <Text className="block text-xs whitespace-pre-wrap">
                {issue.message}
              </Text>
            </div>
          ))}
        </div>
      }
    >
      {badge}
    </Popover>
  );
};
